/**
 * Changelog — "You asked, we did" (M2-DESIGN §6).
 *
 * The credited feedback ids are the point of this screen: they are the proof that a
 * player's sentence turned into a shipped change, so they render even when the server
 * sends nothing else useful.
 */

import type { VNode } from 'preact';
import type { ChangelogEntry } from '../../net/api.js';
import { ListState, Section, loadList, useLoader, when } from './common.js';

export interface ChangelogProps {
  nonce: number;
  token: string;
}

export function Changelog(props: ChangelogProps): VNode {
  const { token } = props;
  const loaded = useLoader<ChangelogEntry[]>(
    () => loadList<ChangelogEntry>('/api/changelog', token, 'changelog'),
    `changelog:${String(props.nonce)}:${token}`,
  );

  // Newest first regardless of the order the server picked.
  const list = [...(loaded.data ?? [])].sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));

  return (
    <div class="hub-pane">
      <Section title="You asked, we did" hint="Every entry credits the feedback that caused it">
        <ListState
          loading={loaded.loading}
          offline={!loaded.loading && loaded.data === null}
          empty={list.length === 0}
          emptyText="Nothing shipped yet. The first entry will name whoever asked for it."
        />
        <div class="hub-list">
          {list.map((e) => {
            const date = when(e.ts);
            const credits = e.feedbackIds ?? [];
            return (
              <article key={String(e.id)} class="hub-card hub-change">
                <h3 class="hub-card-title">{e.title ?? 'Update'}</h3>
                {date.length > 0 ? <span class="hub-card-meta">{date}</span> : null}
                {typeof e.body === 'string' && e.body.length > 0 ? (
                  <p class="hub-card-blurb">{e.body}</p>
                ) : null}
                {credits.length > 0 ? (
                  <div class="hub-card-foot">
                    <span class="hub-label">Asked for in</span>
                    {credits.map((id) => (
                      <span key={String(id)} class="hub-tag">{`#${String(id)}`}</span>
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
